import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import type { CurrentUser } from '@/lib/types'

interface AuthContextType {
  user: CurrentUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string, displayName: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>(null!)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<CurrentUser>('/app/me')
      setUser(data)
    } catch {
      setUser(null)
    }
  }, [])

  useEffect(() => {
    refresh().finally(() => setLoading(false))
  }, [refresh])

  const login = async (email: string, password: string) => {
    await apiFetch('/app/authenticate', {
      method: 'POST',
      body: JSON.stringify({ emailAddress: email, password }),
    })
    await refresh()
  }

  const signup = async (email: string, password: string, displayName: string) => {
    await apiFetch('/app/signup', {
      method: 'POST',
      body: JSON.stringify({ emailAddress: email, password, displayName }),
    })
    await refresh()
  }

  const logout = async () => {
    try {
      await fetch('/api/app/session', { method: 'DELETE', credentials: 'include' })
    } catch {}
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
